import { useAreaIds } from "@/pages/workOrders/hooks/useAreaIds";
import { ATHENS_GRAY } from "@/shared/colors";
import { getCalendarEvents } from "@/shared/repositories/CalendarEventRepository";
import { useDelayAssetsGrouped } from "@/shared/selectors/AssetButtonGroup";
import { getAssetColor, sortAssetsByArea, toSlug } from "@/shared/utils/assets";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	TimeService,
} from "@dexteel/mesf-core";
import { Box, Grid2, LinearProgress, Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarLegend } from "./components/CalendarLegend";
import { DayBoard } from "./components/DayBoard";
import type { DayLine } from "./utils/dayHourGrid";

const moment = getMomentTz();

const errorMessageOf = (e: unknown) => (e instanceof Error ? e.message : null);

/** Wall view: what is active today (all shifts) and tomorrow, one timeline row
 *  per line. Read-only — planning happens in the month planner. */
export const DayDisplayPage = () => {
	const navigate = useNavigate();
	const { areaIds } = useAreaIds();
	const { data: assets, error: assetsError } = useDelayAssetsGrouped();
	const serverTz = TimeService.getInstance().getServerTimeZone();

	const [hoveredRunKey, setHoveredRunKey] = useState<string | null>(null);
	const [error, setError] = useState("");

	// Drives the now-marker and rolls both boards over midnight
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 30_000);
		return () => clearInterval(id);
	}, []);

	const todayKey = moment.tz(now, serverTz).format("YYYY-MM-DD");
	const tomorrowKey = moment
		.tz(now, serverTz)
		.add(1, "day")
		.format("YYYY-MM-DD");
	// The SP cuts the range at a naive midnight compared against UTC datetimes,
	// so one padding day keeps tomorrow's late-night events in; the client clips
	const queryEndKey = moment
		.tz(now, serverTz)
		.add(2, "day")
		.format("YYYY-MM-DD");

	const lines = useMemo(
		() => (assets ? sortAssetsByArea(assets, areaIds) : []),
		[assets, areaIds],
	);

	// Same cache as the month planner: one plan edit invalidates both
	const results = useQueries({
		queries: lines.map((a) => ({
			queryKey: ["calendarEvents", a.AssetId, todayKey, queryEndKey],
			queryFn: ({ signal }: { signal?: AbortSignal }) =>
				getCalendarEvents(
					{ start: todayKey, end: queryEndKey, assetId: a.AssetId },
					signal,
				),
			refetchInterval: 60_000,
			staleTime: 30_000,
			keepPreviousData: true,
			meta: { realtime: ["FAB.GetCalendarEvents"] },
		})),
	});

	// Cheap enough to redo every render, and the row memoizes the real work
	const rows: DayLine[] = lines.map((a, i) => ({
		assetId: a.AssetId,
		assetName: a.AssetName,
		slug: toSlug(a.AssetName),
		accentColor: getAssetColor(a),
		events: results[i]?.data?.events,
		dayShifts: results[i]?.data?.dayShifts,
		isLoading: results[i]?.isLoading ?? true,
		errorMessage: errorMessageOf(results[i]?.error),
	}));
	// 12h D/N lines on top, 8h lines below; sort is stable, so the area order
	// survives inside each group
	const schemeRank = (line: DayLine) =>
		(line.dayShifts ?? []).some(
			(ds) => ds.Date.slice(0, 10) === todayKey && ds.Shift?.startsWith("D"),
		)
			? 0
			: 1;
	rows.sort((a, b) => schemeRank(a) - schemeRank(b));

	// A refetch that fails while the board still has data stays on the row's
	// warning icon; only a line that never loaded is worth a modal
	const blockingError =
		assetsError ?? results.find((r) => r.error && !r.data)?.error;
	const blockingMessage = errorMessageOf(blockingError);
	const shownErrorRef = useRef<string | null>(null);
	useEffect(() => {
		if (!blockingMessage) {
			shownErrorRef.current = null;
			return;
		}
		// The 60s refetch would otherwise reopen the modal on every failed round
		if (blockingMessage === shownErrorRef.current) return;
		shownErrorRef.current = blockingMessage;
		setError(blockingMessage);
	}, [blockingMessage]);

	// Column index of "now": real hours since midnight, so it stays aligned
	// with the grid's hour buckets on DST days
	const dayStart = moment.tz(todayKey, "YYYY-MM-DD", serverTz);
	const nowHour = Math.min(
		23,
		Math.max(0, Math.floor(moment.tz(now, serverTz).diff(dayStart, "hours"))),
	);

	const boardTitle = (dateKey: string, prefix: string) =>
		`${prefix} — ${moment.tz(dateKey, "YYYY-MM-DD", serverTz).format("dddd, MMM D")}`;

	const isRefreshing =
		results.some((r) => r.isFetching) && !results.some((r) => r.isLoading);
	const goToPlanner = useCallback(
		(slug: string) => navigate(`/calendar/${slug}`),
		[navigate],
	);

	return (
		<>
			<HelmetDexteel title="Production Day Display" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				pb={2}
				style={{ backgroundColor: ATHENS_GRAY, userSelect: "none" }}
			>
				<Grid2
					size={{ md: 12, xs: 12 }}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Production Day Display
					</Typography>
					<CalendarLegend />
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }} sx={{ position: "relative" }}>
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							zIndex: 3,
						}}
					>
						{isRefreshing && <LinearProgress />}
					</Box>
					<DayBoard
						title={boardTitle(todayKey, "Today")}
						lines={rows}
						dateKey={todayKey}
						tz={serverTz}
						nowMs={now}
						nowHour={nowHour}
						hoveredRunKey={hoveredRunKey}
						onRunHover={setHoveredRunKey}
						onLineClick={goToPlanner}
					/>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<DayBoard
						title={boardTitle(tomorrowKey, "Tomorrow")}
						lines={rows}
						dateKey={tomorrowKey}
						tz={serverTz}
						nowMs={null}
						nowHour={null}
						hoveredRunKey={hoveredRunKey}
						onRunHover={setHoveredRunKey}
						onLineClick={goToPlanner}
					/>
				</Grid2>
			</Grid2>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
